import "../../../resources/less/components/Pagination.less";
import JQuery from "jquery";
import BaseCCatalogList from "./BaseCCatalogList";
import BaseMCatalogTemplate from "./BaseMCatalogTemplate";

class CCatalogListPagination {
  private listController: BaseCCatalogList;
  private readonly nbElemsPerPage: number;
  private activePageNumber: number;
  private nbPages: number;
  private readonly navTabsAmountBefore: number;
  private readonly navTabsAmountAfter: number;
  private GoToFirstNavTab: JQuery<HTMLElement>;
  private GoToPreviousNavTab: JQuery<HTMLElement>;
  private GoToNextNavTab: JQuery<HTMLElement>;
  private GoToLastNavTab: JQuery<HTMLElement>;

  constructor(listController: BaseCCatalogList) {
    this.listController = listController;
    this.nbElemsPerPage = 9;
    this.activePageNumber = 0;
    this.nbPages = this.getNumberOfPages();
    this.navTabsAmountBefore = 2;
    this.navTabsAmountAfter = 2;

    this.GoToFirstNavTab = $(".pagination-row .first-page-link");
    this.GoToPreviousNavTab = $(".pagination-row .previous-page-link");
    this.GoToNextNavTab = $(".pagination-row .next-page-link");
    this.GoToLastNavTab = $(".pagination-row .last-page-link");

    this.GoToFirstNavTab.on("click", () => {
      this.SwitchPage(0);
    });
    this.GoToPreviousNavTab.on("click", () => {
      this.SwitchPage(this.activePageNumber - 1);
    });
    this.GoToNextNavTab.on("click", () => {
      this.SwitchPage(this.activePageNumber + 1);
    });
    this.GoToLastNavTab.on("click", () => {
      this.SwitchPage(this.nbPages - 1);
    });

    this.applyUrlPagination();
  }

  public UpdateList(): void {
    this.nbPages = this.getNumberOfPages();
    this.SwitchPage(0);
  }

  public SwitchPage(newPageNumber: number): void {
    if (this.listController.isServerDriven) {
      // The server owns paging: ask for the page (1-based); the response redraws the tabs via
      // ShowServerPage. Bounds are the server-reported page count.
      if (
        newPageNumber < 0 ||
        newPageNumber >= this.listController.serverPageCount
      ) {
        return;
      }
      this.listController.LoadServerPage(newPageNumber + 1);
      return;
    }

    if (newPageNumber < 0 || newPageNumber > this.nbPages) return;

    this.setActivePage(newPageNumber);
    this.refreshTabs();
    this.listController.UpdateElementsToDisplayOnPage(this.getElementsOnPage());
  }

  /**
   * Sync the tab UI to a server-rendered page (0-based) after a fetch. Unlike SwitchPage this does
   * not trigger a new request — the page content is already in the DOM.
   */
  public ShowServerPage(activePageNumber: number): void {
    this.nbPages = this.listController.serverPageCount;
    this.setActivePage(activePageNumber);
    this.refreshTabs();
  }

  private getElementsOnPage(): BaseMCatalogTemplate[] {
    const startPosition = this.activePageNumber * this.nbElemsPerPage;
    const endPosition = startPosition + this.nbElemsPerPage;

    return this.listController.filteredTemplates.slice(
      startPosition,
      endPosition,
    );
  }

  private refreshTabs(): void {
    this.checkForPaginationLimits();
    this.createTabs();
  }

  private createTabs(): void {
    const paginationContainer = $(".pagination-container");
    const paginationTemplate = $(".pagination-tab.template");
    const navTabsRange = this.getPagesTabRange();

    // Cleans previous tabs
    paginationContainer.empty();

    // Creates pagination tabs
    for (let i = 0; i < navTabsRange.length; i++) {
      const index = navTabsRange[i];
      const span = paginationTemplate.clone();
      span.attr("data-page", index);
      span.children("a").text(index + 1);
      span.removeClass("hide template");

      // Sets active tab
      if (index === this.activePageNumber) {
        span.children("a").addClass("current");
      }

      // Add Listener
      span.on("click", () => {
        this.SwitchPage(index);
      });
      paginationContainer.append(span);
    }
  }

  private getPagesTabRange(): number[] {
    let nbOfPagesBeforeActivePage = this.navTabsAmountBefore;
    let nbOfPagesAfterActivePage = this.navTabsAmountAfter;
    const navTabsRange: number[] = [];

    if (this.activePageNumber < nbOfPagesBeforeActivePage) {
      nbOfPagesAfterActivePage +=
        nbOfPagesBeforeActivePage - this.activePageNumber;
    }

    if (this.activePageNumber >= this.nbPages - 1 - nbOfPagesAfterActivePage) {
      nbOfPagesBeforeActivePage -=
        this.nbPages - 1 - this.activePageNumber - nbOfPagesAfterActivePage;
    }

    for (
      let j = this.activePageNumber - nbOfPagesBeforeActivePage;
      j <= this.activePageNumber + nbOfPagesAfterActivePage;
      j++
    ) {
      if (j >= 0 && j < this.nbPages) {
        navTabsRange.push(j);
      }
    }
    return navTabsRange;
  }

  private checkForEndOfPagination(): void {
    if (this.activePageNumber === this.nbPages - 1) {
      this.GoToNextNavTab.addClass("disabled");
      this.GoToLastNavTab.addClass("disabled");
    } else {
      this.GoToNextNavTab.removeClass("disabled");
      this.GoToLastNavTab.removeClass("disabled");
    }
  }

  private checkForEmptyList(): boolean {
    const isEmpty = this.listController.isServerDriven
      ? this.listController.serverPageCount === 0
      : this.listController.filteredTemplates.length === 0;
    if (isEmpty) {
      this.GoToPreviousNavTab.addClass("disabled");
      this.GoToFirstNavTab.addClass("disabled");
      this.GoToNextNavTab.addClass("disabled");
      this.GoToLastNavTab.addClass("disabled");
      return true;
    }
    this.GoToPreviousNavTab.removeClass("disabled");
    this.GoToFirstNavTab.removeClass("disabled");
    this.GoToNextNavTab.removeClass("disabled");
    this.GoToLastNavTab.removeClass("disabled");
    return false;
  }

  private checkForBeginningOfPagination(): void {
    if (this.activePageNumber === 0) {
      this.GoToPreviousNavTab.addClass("disabled");
      this.GoToFirstNavTab.addClass("disabled");
    } else {
      this.GoToPreviousNavTab.removeClass("disabled");
      this.GoToFirstNavTab.removeClass("disabled");
    }
  }

  private checkForPaginationLimits(): void {
    if (!this.checkForEmptyList()) {
      this.checkForBeginningOfPagination();
      this.checkForEndOfPagination();
    }
  }

  private getNumberOfPages(): number {
    if (this.listController.isServerDriven) {
      return this.listController.serverPageCount;
    }
    return Math.ceil(
      this.listController.filteredTemplates.length / this.nbElemsPerPage,
    );
  }

  private setActivePage(pageNumber: number): void {
    this.activePageNumber = pageNumber;
    this.updateUrl();
  }

  /**
   * Add/remove the current page in URL
   * @private
   */
  private updateUrl(): void {
    const url = new URL(window.location.href);
    if (this.activePageNumber === 0) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", (this.activePageNumber + 1).toString());
    }
    window.history.pushState({ type: "page" }, "", url.href);
  }

  private applyUrlPagination(): void {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const page = urlParams.get("page");
    this.SwitchPage(page ? +page - 1 : 0);
  }
}

export default CCatalogListPagination;
